import { useCallback, useState, useMemo } from "react";
import { keepPreviousData } from "@tanstack/react-query";

import { Breadcrumbs } from "ui/breadcrumbs";
import CloneProgramTemplatesModal from "components/modals/clone-program-templates-modal/clone-program-templates-modal";
import CreateTouchpointFromProgramTemplateModal from "components/modals/create-touchpoint-from-program-template-modal/create-touchpoint-from-program-template-modal";
import FiltersOnProgramTemplatesTable from "containers/admin/clients/client/program-templates-page/components/filters-on-program-templates-table/filters-on-program-templates-table";
import NewProgramTemplateDropdown from "containers/admin/clients/client/program-templates-page/components/new-program-template-dropdown/new-program-template-dropdown";
import ProgramTemplatesTable from "containers/admin/clients/client/program-templates-page/components/program-templates-table/program-templates-table";
import ProgramTemplatesContextMenu from "containers/admin/clients/client/program-templates-page/components/program-templates-context-menu/program-templates-context-menu";
import ControlBar from "components/partials/control-bar/control-bar";
import { TableSearchAndFilters, FillHeightPage, TableContainer } from "ui/data-grid";

import { useProgramTemplates } from "containers/admin/clients/client/program-templates-page/hooks/use-program-templates";
import { useProgramTemplatesCampaignId } from "containers/admin/clients/client/program-templates-page/hooks/use-program-templates-campaign-id";
import { useArchiveTouchpointModal } from "containers/admin/clients/campaign/campaign-creative-page/hooks/modals/use-archive-touchpoint-modal";
import { useCurrentClient } from "hooks/api/use-clients";
import { getClientPaths } from "utilities/app-routes";
import { AllSelectedData } from "containers/admin/clients/client/program-templates-page/components/program-templates-table/program-templates-table.helper";
import { useUserLayout, useDebouncedUpdateUserLayout } from "hooks/api/use-user-layout";
import { canCreateTouchpointFromTemplate } from "utilities/touchpoint";

import type { FiltersToApply } from "types/filters";
import type {
  GetAllProgramTemplatesParams,
  GetAllProgramTemplatesTypeInItem,
} from "generated/schemas";
import type { ColumnOption } from "types/table";
import type { TouchpointAttributes } from "hooks/api/use-touchpoints";

const TABLE_CONFIG_KEY = "programTemplates";

const PROGRAM_TEMPLATES_COLUMN_OPTIONS: ColumnOption[] = [
  { id: "type", label: "Type" },
  { id: "latestVersion", label: "Newest version status" },
  { id: "lastModifiedDate", label: "Last updated" },
];

const DEFAULT_PROGRAM_TEMPLATES_FILTERS_TO_APPLY = { type: [] };
const PROGRAM_TEMPLATES_FILTER_ID_TO_TITLE: Record<string, string> = {
  type: "Template Type",
};

const ProgramTemplatesPage = () => {
  const currentClient = useCurrentClient();
  const paths = getClientPaths({ clientId: currentClient?.id });

  const { data: tableColumnsConfig = {} } = useUserLayout();
  const { toggleColumnVisibility } = useDebouncedUpdateUserLayout();

  const hiddenColumnIds = useMemo(
    () => new Set(tableColumnsConfig[TABLE_CONFIG_KEY]?.hidden ?? []),
    [tableColumnsConfig],
  );

  const handleToggleColumn = useCallback(
    (columnId: string) => toggleColumnVisibility(TABLE_CONFIG_KEY, columnId),
    [toggleColumnVisibility],
  );
  const { data: programTemplatesCampaignId, isPending: isCampaignIdLoading } =
    useProgramTemplatesCampaignId(currentClient?.id);

  // Search and Filters state (Sorting is now local in the table)
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [filters, setFilters] = useState<GetAllProgramTemplatesParams>({});

  // Selection state management
  const [allSelectedData, setAllSelectedData] = useState<AllSelectedData[]>([
    { isAllSelected: false, page: 0, selectedTouchpoints: [] },
  ]);

  const queryParams = useMemo(
    (): GetAllProgramTemplatesParams => ({
      q: searchTerm || undefined,
      ...filters,
    }),
    [searchTerm, filters],
  );

  const {
    programTemplates,
    isLoading: isTemplatesLoading,
    isFetching: isTemplatesFetching,
    totalElements,
    refetch,
  } = useProgramTemplates(queryParams, {
    enabled: !isCampaignIdLoading && !!programTemplatesCampaignId,
    placeholderData: keepPreviousData,
  });

  // Convert filters to FiltersToApply format for AppliedFilters component
  const filtersToApply: Record<string, string[]> = useMemo(
    () => ({
      ...(filters["type.in"] && { type: filters["type.in"] as string[] }),
    }),
    [filters],
  );

  const setFiltersToApply = useCallback((newFilters: FiltersToApply) => {
    const newType = "type" in newFilters && newFilters.type?.length ? newFilters.type : undefined;
    setFilters(newType ? { "type.in": newType as GetAllProgramTemplatesTypeInItem[] } : {});
  }, []);

  const allSelectedAmount = useMemo(() => {
    return allSelectedData.reduce((total, pageData) => {
      return total + pageData.selectedTouchpoints.length;
    }, 0);
  }, [allSelectedData]);

  const flattenedSelectedTemplates = useMemo(
    () => allSelectedData.flatMap((d) => d.selectedTouchpoints),
    [allSelectedData],
  );

  const approvedSelectedTemplates = useMemo(
    () =>
      flattenedSelectedTemplates.filter((template) =>
        canCreateTouchpointFromTemplate(template.latestVersion?.status),
      ),
    [flattenedSelectedTemplates],
  );

  const [isCloneTemplatesModalOpen, setIsCloneTemplatesModalOpen] = useState(false);
  const [isCreateFromTemplateModalOpen, setIsCreateFromTemplateModalOpen] = useState(false);

  const removeSelectedTemplate = useCallback((templateId: string) => {
    setAllSelectedData((prev) =>
      prev.map((pageData) => ({
        ...pageData,
        isAllSelected: false,
        selectedTouchpoints: pageData.selectedTouchpoints.filter(({ id }) => id !== templateId),
      })),
    );
  }, []);

  const { openArchiveModal, archiveModalMarkup } = useArchiveTouchpointModal({
    onSuccess: () => {
      refetch();
    },
    onRemoveSelectedItem: removeSelectedTemplate,
    apiContext: { campaignId: programTemplatesCampaignId },
    isTemplate: true,
  });

  const handleArchiveTemplates = () =>
    openArchiveModal(
      flattenedSelectedTemplates.map(
        ({ id, name, type }) => ({ id, name, type }) as TouchpointAttributes,
      ),
    );

  const actionSlot = (
    <div className="flex items-center gap-2.5">
      <ProgramTemplatesContextMenu
        allSelectedAmount={allSelectedAmount}
        totalTemplates={totalElements}
        hasApprovedSelected={approvedSelectedTemplates.length > 0}
        onCloneTemplates={() => setIsCloneTemplatesModalOpen(true)}
        onCreateTouchpointsFromTemplates={() => setIsCreateFromTemplateModalOpen(true)}
        onArchiveTemplates={handleArchiveTemplates}
      />
      {!allSelectedAmount && (
        <ControlBar variant="inset">
          <NewProgramTemplateDropdown onTemplateCreated={refetch} />
        </ControlBar>
      )}
    </div>
  );

  return (
    <FillHeightPage>
      <Breadcrumbs
        breadcrumbs={[
          { label: currentClient?.name, href: paths.clientCampaignsPath },
          { label: "Creative templates", active: true },
        ]}
      />

      <TableContainer fillHeight>
        <TableSearchAndFilters
          setCurrentSearchTerm={setSearchTerm}
          filtersToApply={filtersToApply}
          setFiltersToApply={setFiltersToApply}
          defaultFiltersToApply={DEFAULT_PROGRAM_TEMPLATES_FILTERS_TO_APPLY}
          filterIdToTitle={PROGRAM_TEMPLATES_FILTER_ID_TO_TITLE}
          tableFilters={
            <FiltersOnProgramTemplatesTable
              filtersToApply={filtersToApply}
              setFiltersToApply={setFiltersToApply}
              tableColumnsConfig={tableColumnsConfig}
              columnOptions={PROGRAM_TEMPLATES_COLUMN_OPTIONS}
              onToggleColumn={handleToggleColumn}
              isFetching={isTemplatesFetching}
            />
          }
          actionSlot={actionSlot}
        />

        <ProgramTemplatesTable
          programTemplatesCampaignId={programTemplatesCampaignId ?? ""}
          programTemplates={programTemplates}
          isLoading={isTemplatesLoading || isCampaignIdLoading}
          isFetching={isTemplatesFetching}
          allSelectedData={allSelectedData}
          setAllSelectedData={setAllSelectedData}
          onRefetch={refetch}
          hiddenColumnIds={hiddenColumnIds}
        />
      </TableContainer>

      {flattenedSelectedTemplates.length > 0 && (
        <CloneProgramTemplatesModal
          isOpen={isCloneTemplatesModalOpen}
          onClose={() => setIsCloneTemplatesModalOpen(false)}
          selectedTemplates={flattenedSelectedTemplates}
          onSuccess={() => {
            refetch();
            setAllSelectedData([{ isAllSelected: false, page: 0, selectedTouchpoints: [] }]);
          }}
        />
      )}

      {approvedSelectedTemplates.length > 0 && (
        <CreateTouchpointFromProgramTemplateModal
          isOpen={isCreateFromTemplateModalOpen}
          onClose={() => setIsCreateFromTemplateModalOpen(false)}
          onSuccess={() => {
            setAllSelectedData([{ isAllSelected: false, page: 0, selectedTouchpoints: [] }]);
          }}
          selectedTemplates={approvedSelectedTemplates}
        />
      )}

      {archiveModalMarkup}
    </FillHeightPage>
  );
};

export default ProgramTemplatesPage;
